import { Link } from '#app/components/Link/index.js';
import { Menu } from '#app/components/Menu/Menu.js';
import { useIsMediumScreen } from '#app/hooks/useIsMediumScreen.js';
import {
  CloseIcon,
  EditAltIcon,
  EditIcon,
  MoreIcon,
  MoveIcon,
  TrashIcon,
} from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import { type DraggableSyntheticListeners } from '@dnd-kit/core';
import { css, styled } from 'styled-components';

const MobileMenuButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    color: ${theme.colorsV2.black};

    padding: 8px;
    border-radius: 86px;
    border: 1px solid ${theme.colorsV2.gray40};
    background-color: ${theme.colorsV2.white};
  `}
`;

const MobileMenuButtons = styled.div<{
  $addGutter?: boolean;
}>`
  ${({ $addGutter, theme }) => css`
    height: fit-content;
    width: fit-content;
    display: flex;
    flex-direction: column;
    align-items: center;

    grid-area: coverSectionChild;

    gap: 4px;
    padding: 4px;
    margin-left: auto;
    margin-bottom: auto;

    ${$addGutter &&
    css`
      margin-right: 4px;
      margin-top: 4px;
    `}

    border-radius: 76px;
    border: 0.75px solid ${theme.colorsV2.gray30};
    background-color: ${theme.colorsV2.white};
    z-index: 1;
  `}
`;

const MobileCardSortHandle = styled.button`
  ${({ theme }) => css`
    display: flex;

    padding: 8px;
    border-radius: 86px;
    border: 1px solid ${theme.colorsV2.gray40};
    background-color: ${theme.colorsV2.white};

    cursor: move;
    touch-action: none;
  `};
`;

export const MaybeMobileEditableCardActionsMenu = ({
  canEdit,
  editMenuOpen,
  isProfileMagicSuggestion,
  onDismissSuggestion,
  openDeleteModal,
  slug,
  sortableListeners,
  toggleMenu,
  variant,
}: {
  readonly canEdit: boolean;
  readonly editMenuOpen: boolean;
  readonly isProfileMagicSuggestion: boolean;
  readonly onDismissSuggestion: () => void;
  readonly openDeleteModal: () => void;
  readonly slug: string;
  readonly sortableListeners: DraggableSyntheticListeners;
  readonly toggleMenu: () => void;
  readonly variant: 'project' | 'service';
}) => {
  const isBelowMedium = useIsMediumScreen();

  const editRoute =
    variant === 'project'
      ? getRoute('/p/@slug/edit', { args: { slug } })
      : getRoute('/s/@slug/edit', { args: { slug } });

  if (!canEdit || !isBelowMedium) {
    return null;
  }

  if (isProfileMagicSuggestion) {
    return (
      <MobileMenuButtons>
        <MobileCardSortHandle
          tabIndex={0}
          {...sortableListeners}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <MoveIcon size={24} />
        </MobileCardSortHandle>
        <Menu
          isOpen={editMenuOpen}
          placement="top"
          toggleMenu={toggleMenu}
        >
          <Menu.Reference>
            <MobileMenuButton
              aria-label={`Open ${variant} menu`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                toggleMenu();
              }}
            >
              <MoreIcon size={24} />
            </MobileMenuButton>
          </Menu.Reference>
          <Menu.Panel>
            <Menu.Item
              as={Link}
              icon={EditAltIcon}
              to={editRoute}
            >
              Add content
            </Menu.Item>

            <Menu.Item
              icon={CloseIcon}
              onClick={onDismissSuggestion}
            >
              Dismiss suggestion
            </Menu.Item>
          </Menu.Panel>
        </Menu>
      </MobileMenuButtons>
    );
  }

  return (
    <MobileMenuButtons $addGutter={variant === 'project'}>
      <MobileCardSortHandle
        tabIndex={0}
        {...sortableListeners}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <MoveIcon size={24} />
      </MobileCardSortHandle>
      <Menu
        isOpen={editMenuOpen}
        placement="bottom-end"
        toggleMenu={toggleMenu}
      >
        <Menu.Reference>
          <MobileMenuButton
            aria-label={`Open ${variant} menu`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              toggleMenu();
            }}
          >
            <MoreIcon size={24} />
          </MobileMenuButton>
        </Menu.Reference>
        <Menu.Panel>
          <Menu.Item
            as={Link}
            icon={EditIcon}
            to={editRoute}
          >
            Edit
          </Menu.Item>

          <Menu.Item
            destructive
            icon={TrashIcon}
            onClick={openDeleteModal}
          >
            Delete
          </Menu.Item>
        </Menu.Panel>
      </Menu>
    </MobileMenuButtons>
  );
};
